/* Emergency Job Loss Savings vault — full lifecycle in one component.
 *
 * User flow:
 *   1) Create vault (name + target amount)
 *   2) Keep depositing until balance >= target
 *   3) Upload proof of job loss (PDF/PNG/JPG). Claude Sonnet 4.6 verifies.
 *   4) Once approved, Withdraw button unlocks.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AutofillToggle, useAutofillRules } from "@/components/VaultControls";
import {
  ShieldAlert, Upload, ArrowDownToLine, ArrowUpFromLine,
  CheckCircle2, XCircle, Loader2, Plus, FileText,
  CalendarClock, AlertTriangle, ShieldCheck, RotateCw,
} from "lucide-react";

function fmtFriday(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
}

function RequirementBanner() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const jl = user?.job_loss_contribution;
  if (!jl || !jl.applicable) return null;
  const amount = Number(jl.contribution_amount ?? 100);
  const paid = jl.paid_this_period;

  const retry = async () => {
    setBusy(true);
    try {
      await api.post("/auth/job-loss/retry");
      if (refresh) await refresh();
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  if (paid) {
    return (
      <div className="mb-4 flex items-start gap-3 bg-forest/8 border border-forest/30 rounded-xl px-4 py-3 text-sm"
           data-testid="jobloss-banner-paid">
        <ShieldCheck className="w-4 h-4 text-forest shrink-0 mt-0.5" strokeWidth={1.8} />
        <div className="text-ink">
          Rainy-day contribution of <strong>${amount.toFixed(2)}</strong> for the period beginning
          <strong> {fmtFriday(jl.period)}</strong> is paid.
          {jl.next_due && <span className="text-moss"> Next Friday due: <strong>{fmtFriday(jl.next_due)}</strong>.</span>}
        </div>
      </div>
    );
  }
  return (
    <div className="mb-4 flex items-start gap-3 bg-clay-light border border-clay/30 rounded-xl px-4 py-3 text-sm"
         data-testid="jobloss-banner-due">
      <AlertTriangle className="w-4 h-4 text-clay shrink-0 mt-0.5" strokeWidth={1.8} />
      <div className="flex-1 text-ink">
        <div>
          <strong>Mandatory ${amount.toFixed(2)} rainy-day contribution</strong> is due
          {jl.period ? <> for the biweekly Friday period beginning <strong>{fmtFriday(jl.period)}</strong></> : jl.next_due ? <> starting <strong>{fmtFriday(jl.next_due)}</strong></> : null}.
        </div>
        <div className="flex items-center gap-1.5 text-xs text-moss mt-1">
          <CalendarClock className="w-3 h-3" strokeWidth={1.8} />
          Monthly on payday · pulled from your Spending account.
        </div>
        {jl.last_error && (
          <div className="text-xs text-clay mt-1">
            Last attempt failed: <span className="font-mono">{jl.last_error}</span>. Make sure Spending has at least ${amount.toFixed(2)} and tap retry.
          </div>
        )}
      </div>
      <button onClick={retry} disabled={busy}
        data-testid="jobloss-retry-btn"
        className="inline-flex items-center gap-1.5 bg-forest text-white rounded-md px-3 py-1.5 text-xs font-medium hover:bg-forest-deep disabled:opacity-60 shrink-0">
        <RotateCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> {busy ? "Charging…" : "Retry now"}
      </button>
    </div>
  );
}

export default function JobLossVault() {
  const { user, refresh } = useAuth();
  const autofill = useAutofillRules();
  const [vaults, setVaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState("");
  const [openDeposit, setOpenDeposit] = useState(null);   // vault obj
  const [openVerify, setOpenVerify] = useState(null);
  const [openWithdraw, setOpenWithdraw] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vaults/job-loss");
      setVaults(data || []);
    } catch (e) {
      setMsg(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCreated = () => { setCreating(false); load(); };

  return (
    <section className="mt-10" data-testid="job-loss-section">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Emergency</div>
          <h2 className="font-serif text-2xl tracking-tight mt-1">Job-loss savings</h2>
          <p className="text-xs text-moss mt-1 max-w-xl">
            Set a target, keep adding. Funds unlock only after you upload proof of job loss —
            our AI reviews the document and releases the money when verified.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 bg-ink text-white rounded-md px-3 py-2 text-xs font-medium hover:bg-forest-deep transition-colors"
          data-testid="job-loss-new-btn">
          <Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> New goal
        </button>
      </div>

      {msg && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-clay-light border border-clay/40 text-xs text-clay"
             data-testid="job-loss-msg">{msg}</div>
      )}

      <RequirementBanner />

      {loading ? (
        <div className="text-sage text-sm">Loading…</div>
      ) : vaults.length === 0 ? (
        <div className="border border-dashed border-sand rounded-xl p-6 text-center text-sm text-sage"
             data-testid="job-loss-empty">
          No emergency job-loss goals yet. Create one to start building a safety cushion
          that only unlocks with verified proof of layoff.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="job-loss-list">
          {vaults.map((v) => (
            <JobLossCard
              key={v.id} v={v}
              onDeposit={() => { setMsg(""); setOpenDeposit(v); }}
              onVerify={() => { setMsg(""); setOpenVerify(v); }}
              onWithdraw={() => { setMsg(""); setOpenWithdraw(v); }}
              quickUsed={user?.rainy_day_quick_release_used}
              autofill={autofill.rules[v.id]}
              onAutofill={autofill.reload}
              onChanged={() => { load(); refresh && refresh(); window.dispatchEvent(new Event("bill-locks:refresh")); }}
              onMsg={setMsg}
              onQuickRelease={async (vv) => {
                setMsg("");
                try { const { data } = await api.post(`/vaults/job-loss/${vv.id}/quick-release`); setMsg(`${fmtMoney(data.released)} moved to Spending. Your one-time release is now used.`); load(); refresh && refresh(); window.dispatchEvent(new Event("bill-locks:refresh")); }
                catch (e) { setMsg(formatErr(e.response?.data?.detail) || e.message); }
              }}
            />
          ))}
        </div>
      )}

      {creating && <CreateDialog onClose={() => setCreating(false)} onDone={onCreated} />}
      {openDeposit && (
        <DepositDialog v={openDeposit}
          onClose={() => setOpenDeposit(null)}
          onDone={() => { setOpenDeposit(null); load(); }} />
      )}
      {openVerify && (
        <VerifyDialog v={openVerify}
          onClose={() => setOpenVerify(null)}
          onDone={() => { setOpenVerify(null); load(); }} />
      )}
      {openWithdraw && (
        <WithdrawDialog v={openWithdraw}
          onClose={() => setOpenWithdraw(null)}
          onDone={() => { setOpenWithdraw(null); load(); }} />
      )}
    </section>
  );
}


function JobLossCard({ v, onDeposit, onVerify, onWithdraw, quickUsed, onQuickRelease, autofill, onAutofill, onChanged, onMsg }) {
  const [confirmQuick, setConfirmQuick] = useState(false);
  const pct = Math.min(100, Math.max(0, v.progress || 0));
  const verified = v.job_loss_verified;
  const goalReached = v.goal_reached;
  const lastAttempt = (v.verification_attempts || []).slice(-1)[0];
  return (
    <div className="bg-linen border border-sand rounded-2xl p-6"
         data-testid={`job-loss-card-${v.id}`}>
      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${
        verified ? "bg-forest text-white" : "bg-clay-light text-clay"
      }`}>
        <ShieldAlert className="w-3 h-3" strokeWidth={2} />
        {verified ? "Unlocked · AI verified" : "Locked until verified"}
      </div>
      <h3 className="font-serif text-2xl mt-3 break-words" data-testid={`job-loss-name-${v.id}`}>{v.name}</h3>

      <div className="mt-4">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Balance</div>
            <div className="font-mono text-3xl text-ink mt-0.5"
                 data-testid={`job-loss-balance-${v.id}`}>{fmtMoney(v.balance)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Goal</div>
            <div className="font-mono text-base text-moss mt-0.5"
                 data-testid={`job-loss-target-${v.id}`}>{fmtMoney(v.target_amount)}</div>
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-sand-warm overflow-hidden">
          <div className="h-full bg-forest" style={{ width: `${pct}%` }}
               data-testid={`job-loss-progress-${v.id}`} />
        </div>
        <div className="mt-1 text-[10px] text-sage font-mono">{pct.toFixed(1)}% funded</div>
      </div>

      {lastAttempt && !verified && (
        <div className="mt-3 px-2.5 py-2 rounded-lg bg-clay-light border border-clay/30 text-[11px] text-clay"
             data-testid={`job-loss-last-attempt-${v.id}`}>
          <div className="font-medium flex items-center gap-1">
            <XCircle className="w-3 h-3" strokeWidth={2} />
            Last upload rejected · {lastAttempt.doc_type || "unknown"} · confidence {(Number(lastAttempt.confidence)||0).toFixed(2)}
          </div>
          <div className="mt-0.5 text-moss">{lastAttempt.reasoning || "Please upload a clearer document."}</div>
        </div>
      )}
      {verified && (
        <div className="mt-3 px-2.5 py-2 rounded-lg bg-mint-light border border-forest/40 text-[11px] text-forest"
             data-testid={`job-loss-verified-${v.id}`}>
          <div className="font-medium flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
            Verified · {(v.verified_doc_type || "").replace(/_/g, " ")}
          </div>
          <div className="mt-0.5 text-moss text-[10px]">
            Approved {v.verified_at ? new Date(v.verified_at).toLocaleDateString() : ""}
          </div>
        </div>
      )}

      <div className="mt-4 flex gap-2 flex-wrap">
        <button onClick={onDeposit} disabled={goalReached}
          className="flex-1 min-w-[110px] bg-forest text-white rounded-md px-3 py-1.5 text-xs font-medium hover:bg-forest-deep disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-1.5"
          data-testid={`job-loss-deposit-btn-${v.id}`}>
          <ArrowDownToLine className="w-3 h-3" strokeWidth={1.5} /> Deposit
        </button>
        <button onClick={onVerify} disabled={verified}
          className="flex-1 min-w-[110px] bg-transparent border border-ink text-ink rounded-md px-3 py-1.5 text-xs font-medium hover:bg-ink hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-1.5"
          data-testid={`job-loss-verify-btn-${v.id}`}>
          <Upload className="w-3 h-3" strokeWidth={1.5} /> Upload proof
        </button>
        {!quickUsed && v.balance >= 160 && (
          confirmQuick ? (
            <div className="w-full flex items-center gap-2 flex-wrap text-[11px]" data-testid={`job-loss-quick-confirm-${v.id}`}>
              <span className="text-clay">Move $160 to Spending now? This is your only no-proof release, ever.</span>
              <button onClick={() => { setConfirmQuick(false); onQuickRelease(v); }} className="bg-clay text-white rounded-md px-3 py-1.5 text-xs font-medium" data-testid={`job-loss-quick-yes-${v.id}`}>Yes, move $160</button>
              <button onClick={() => setConfirmQuick(false)} className="text-sage hover:text-ink" data-testid={`job-loss-quick-no-${v.id}`}>Cancel</button>
            </div>
          ) : (
            <button onClick={() => setConfirmQuick(true)}
              className="flex-1 min-w-[110px] bg-ink text-cream rounded-md px-3 py-1.5 text-xs font-medium hover:bg-ink-deep transition-colors inline-flex items-center justify-center gap-1.5"
              data-testid={`job-loss-quick-btn-${v.id}`}>
              <ArrowUpFromLine className="w-3 h-3" strokeWidth={1.5} /> $160 to Spending · one time
            </button>
          )
        )}
        <button onClick={onWithdraw} disabled={!verified || v.balance <= 0}
          className="flex-1 min-w-[110px] bg-transparent border border-gold text-gold rounded-md px-3 py-1.5 text-xs font-medium hover:bg-gold hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-1.5"
          data-testid={`job-loss-withdraw-btn-${v.id}`}>
          <ArrowUpFromLine className="w-3 h-3" strokeWidth={1.5} /> Withdraw
        </button>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button onClick={async () => {
            onMsg("");
            try {
              const { data } = await api.post("/vaults/job-loss/sweep-now");
              onMsg(data.status === "charged" ? `${fmtMoney(data.amount)} swept into your Rainy-day fund.` : data.status === "already_paid" ? "This period's Rainy-day pull is already in." : data.status === "not_yet_started" ? `Pulls start on payday (${data.next_due}).` : `Couldn't sweep: ${data.error || data.status}.`);
              onChanged && onChanged();
            } catch (e) { onMsg(formatErr(e.response?.data?.detail) || e.message); }
          }}
          className="inline-flex items-center gap-1.5 bg-forest text-white rounded-md px-3 py-1.5 text-[11px] font-medium hover:bg-forest-deep transition-colors"
          data-testid={`job-loss-sweep-now-${v.id}`}>
          <RotateCw className="w-3 h-3" strokeWidth={2} /> Sweep now
        </button>
        <span className="text-[10px] text-sage">Pull this period&apos;s contribution from Spending right away.</span>
      </div>
      <div className="mt-3"><AutofillToggle vaultId={v.id} rule={autofill} onChanged={onAutofill} onMsg={onMsg} compact /></div>
    </div>
  );
}


// --- Dialogs --------------------------------------------------------------

function ModalShell({ title, subtitle, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
         data-testid={testid}>
      <div className="bg-cream rounded-2xl border border-sand max-w-md w-full p-8">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-gold">{title}</div>
          <button onClick={onClose} className="text-moss hover:text-ink"
                  data-testid={`${testid}-close`}>✕</button>
        </div>
        {subtitle && <h3 className="font-serif text-2xl mt-3">{subtitle}</h3>}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}


function CreateDialog({ onClose, onDone }) {
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/vaults/job-loss/create", {
        name: name.trim(), target_amount: parseFloat(target),
      });
      onDone();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title="New emergency goal" subtitle="Job-loss savings target" onClose={onClose} testid="job-loss-create">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={60}
            placeholder="Rainy-day fund"
            className="mt-2 w-full bg-white border border-sand rounded-lg px-4 py-3 focus:ring-2 focus:ring-gold focus:border-transparent outline-none"
            data-testid="job-loss-create-name" autoFocus />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Target amount (USD)</label>
          <input type="number" step="0.01" min="1" required value={target} onChange={(e) => setTarget(e.target.value)}
            placeholder="5000"
            className="mt-2 w-full bg-white border border-sand rounded-lg px-4 py-3 font-mono text-lg focus:ring-2 focus:ring-gold focus:border-transparent outline-none"
            data-testid="job-loss-create-target" />
        </div>
        {err && <div className="text-sm text-clay" data-testid="job-loss-create-error">{err}</div>}
        <button type="submit" disabled={busy}
          className="w-full bg-ink text-white rounded-lg px-6 py-3 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors"
          data-testid="job-loss-create-submit">
          {busy ? "Creating…" : "Create goal"}
        </button>
      </form>
    </ModalShell>
  );
}


function DepositDialog({ v, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const remaining = Math.max(0, (v.target_amount || 0) - (v.balance || 0));
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post(`/vaults/job-loss/${v.id}/deposit`, {
        amount: parseFloat(amount),
      });
      onDone();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title={`Deposit → ${v.name}`} subtitle="Add to your emergency fund"
                onClose={onClose} testid="job-loss-deposit">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Amount (USD)</label>
          <input type="number" step="0.01" min="20" max="500" required value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-2 w-full bg-white border border-sand rounded-lg px-4 py-3 font-mono text-lg focus:ring-2 focus:ring-gold focus:border-transparent outline-none"
            placeholder="0.00" data-testid="job-loss-deposit-amount" autoFocus />
          <p className="mt-1.5 text-xs text-sage">
            $20 min · $500 max per transaction · {fmtMoney(remaining)} left to goal
          </p>
        </div>
        {err && <div className="text-sm text-clay" data-testid="job-loss-deposit-error">{err}</div>}
        <button type="submit" disabled={busy}
          className="w-full bg-forest text-white rounded-lg px-6 py-3 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors"
          data-testid="job-loss-deposit-submit">
          {busy ? "Depositing…" : "Deposit"}
        </button>
      </form>
    </ModalShell>
  );
}


function VerifyDialog({ v, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const inputRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Please choose a file."); return; }
    setErr(""); setBusy(true); setVerdict(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (note) fd.append("note", note);
      const { data } = await api.post(`/vaults/job-loss/${v.id}/verify`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setVerdict(data.verdict);
      if (data.approved) {
        setTimeout(onDone, 1500);
      }
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Verify job loss" subtitle="Upload proof — AI reviews it"
                onClose={onClose} testid="job-loss-verify">
      <p className="text-xs text-moss mb-4 leading-relaxed">
        Accepted: termination letter, unemployment approval, COBRA notice, RIF/layoff notification,
        or an employer email confirming your end date. PDF, PNG, or JPG (max 8 MB).
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Document</label>
          <input ref={inputRef} type="file" accept="application/pdf,image/png,image/jpeg,image/jpg"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden" data-testid="job-loss-verify-file" />
          <button type="button" onClick={() => inputRef.current?.click()}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 border border-dashed border-sand rounded-lg px-4 py-6 bg-white hover:bg-linen transition-colors"
            data-testid="job-loss-verify-pick">
            <FileText className="w-4 h-4 text-moss" strokeWidth={1.75} />
            <span className="text-sm text-ink">
              {file ? file.name : "Choose PDF, PNG, or JPG"}
            </span>
          </button>
          {file && (
            <div className="mt-1 text-xs text-sage">
              {(file.size / 1024).toFixed(0)} KB · {file.type || "unknown"}
            </div>
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Note (optional)</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200}
            className="mt-2 w-full bg-white border border-sand rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
            placeholder="Anything the reviewer should know?"
            data-testid="job-loss-verify-note" />
        </div>
        {err && <div className="text-sm text-clay" data-testid="job-loss-verify-error">{err}</div>}

        {verdict && (
          <div className={`px-3 py-3 rounded-lg text-[12px] ${
            verdict.approved
              ? "bg-mint-light border border-forest/40 text-forest"
              : "bg-clay-light border border-clay/40 text-clay"
          }`} data-testid="job-loss-verify-verdict">
            <div className="font-medium flex items-center gap-1.5">
              {verdict.approved
                ? <><CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> Approved — {verdict.doc_type?.replace(/_/g, " ")}</>
                : <><XCircle className="w-3.5 h-3.5" strokeWidth={2} /> Not approved — {verdict.doc_type?.replace(/_/g, " ")}</>}
              <span className="ml-1 text-[10px] font-mono opacity-70">confidence {Number(verdict.confidence || 0).toFixed(2)}</span>
            </div>
            <div className="mt-1 text-moss">{verdict.reasoning}</div>
            {!verdict.approved && (
              <div className="mt-2 text-[11px] text-clay">
                Please try a clearer or more official document.
              </div>
            )}
          </div>
        )}

        <button type="submit" disabled={busy || !file}
          className="w-full bg-ink text-white rounded-lg px-6 py-3 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
          data-testid="job-loss-verify-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.75} />}
          {busy ? "AI reviewing…" : "Submit for review"}
        </button>
      </form>
    </ModalShell>
  );
}


function WithdrawDialog({ v, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post(`/vaults/job-loss/${v.id}/withdraw`, {
        amount: parseFloat(amount),
      });
      onDone();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title={`Emergency withdrawal · ${v.name}`}
                subtitle="Release funds to your bank"
                onClose={onClose} testid="job-loss-withdraw">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Amount (USD)</label>
          <input type="number" step="0.01" min="1" max={v.balance} required value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-2 w-full bg-white border border-sand rounded-lg px-4 py-3 font-mono text-lg focus:ring-2 focus:ring-gold focus:border-transparent outline-none"
            placeholder="0.00" data-testid="job-loss-withdraw-amount" autoFocus />
          <p className="mt-1.5 text-xs text-sage">Available: {fmtMoney(v.balance)}</p>
        </div>
        {err && <div className="text-sm text-clay" data-testid="job-loss-withdraw-error">{err}</div>}
        <button type="submit" disabled={busy}
          className="w-full bg-gold text-white rounded-lg px-6 py-3 font-medium hover:bg-gold-deep disabled:opacity-60 transition-colors"
          data-testid="job-loss-withdraw-submit">
          {busy ? "Releasing…" : "Release funds"}
        </button>
      </form>
    </ModalShell>
  );
}
